import {assetsApi} from '@/api';
import {gt} from '@/helper';
import {getNetwork} from '@wagmi/core';
import {BehaviorSubject, combineLatest, delay, delayWhen, distinctUntilChanged, filter, Observable, repeat, Subject, takeUntil, tap, withLatestFrom} from 'rxjs';
import type {DepositDrawerParams} from '../../utils';
import type {FormatedResult, Currency$, Network$, CurrentNetworkOptions$, DepositAccountType$, CurrentDepositAddress$} from './shared';

export const componentStatus$ = new BehaviorSubject<'' | 'visible' | 'hidden'>('');
const componentUnMount$ = componentStatus$.pipe(filter((componentStatus) => componentStatus !== 'visible'));
const componentMount$ = componentStatus$.pipe(filter((componentStatus) => componentStatus === 'visible'));
const componentDestroy$ = componentStatus$.pipe(filter((componentStatus) => componentStatus === 'hidden'));

export const setDefaultData$ = new Subject<DepositDrawerParams>();

/**
 * 保证当前network是在currency支持的范围内
 */
export function useSafeNetworkEffect(params: {network$: Network$; currentNetworkOptions$: CurrentNetworkOptions$}) {
  const {network$, currentNetworkOptions$} = params;
  currentNetworkOptions$
    .pipe(
      takeUntil(componentUnMount$),
      repeat({
        delay: () => componentMount$,
      })
    )
    .subscribe((currentNetworkOptions) => {
      const network = network$.getValue();
      const haveCurrentNetwork = currentNetworkOptions.find((option) => option.value === network);
      if (!haveCurrentNetwork) {
        network$.next(currentNetworkOptions[0]?.value || '');
      }
    });
}

/**
 * 初始化默认数据
 */
export function useInitDataEffect({invalidData$, currency$, network$}: {invalidData$: Observable<FormatedResult>; currency$: Currency$; network$: Network$}) {
  setDefaultData$
    .pipe(
      delayWhen(() => invalidData$),
      withLatestFrom(invalidData$),
      takeUntil(componentUnMount$),
      repeat({
        delay: () => componentMount$,
      })
    )
    .subscribe(([params, data]) => {
      const {contractAddress, currency, networkId, chainId, chainName} = params;
      const {contractAddressTokenMap, currencyNetworkMap, networkCurrencyOptionsMap, networkSupportCurrenciesMap, chainIdNetworkMap, chainNameNetworkMap} = data;

      let currencyResult = '';
      let networkResult: string | number = '';
      // 1.优先根据合约地址匹配
      if (contractAddress) {
        const matchedToken = contractAddressTokenMap[contractAddress];
        if (matchedToken) {
          const {currency: currencyName, extNetWorkId} = matchedToken;
          currencyResult = currencyName;
          networkResult = extNetWorkId;
        }
      }
      // 2. 根据传入的currency匹配，还要校验传入的currency是否支持
      if (!currencyResult && currency && currencyNetworkMap[currency]?.length > 0) {
        currencyResult = currency;
      }
      // 3.  根据出入的networkId匹配
      if (!networkResult && networkId && networkCurrencyOptionsMap[networkId]?.length > 0) {
        networkResult = networkId;
      }
      // 4. 根据传入的chainId匹配
      if (!networkResult && chainId) {
        const currentNetwork = chainIdNetworkMap[chainId];
        if (currentNetwork) {
          networkResult = currentNetwork.extNetWorkId;
        }
      }
      // 5. 根据传入的chainName匹配
      if (!networkResult && chainName) {
        const currentNetwork = chainNameNetworkMap[chainName];
        if (currentNetwork) {
          networkResult = currentNetwork.extNetWorkId;
        }
      }
      // 6. 根据当前连接的链匹配
      if (!networkResult) {
        const {chain} = getNetwork();
        const extNetWorkId = chain?.id;
        if (networkCurrencyOptionsMap[extNetWorkId]) {
          networkResult = extNetWorkId;
        }
      }
      // 7. 判断currency和network是否匹配
      if (currencyResult && networkResult) {
        const supportNetworks = currencyNetworkMap[currencyResult];
        const isValid = !!supportNetworks?.find((network) => network.extNetWorkId == networkResult);
        if (!isValid) {
          networkResult = supportNetworks[0].extNetWorkId;
        }
      }
      // 8. 没有有效的数据
      if (!currencyResult && !networkResult) {
        const {networkId, currency} = getDefaultNetworkAndCurrency(networkSupportCurrenciesMap);
        currencyResult = currency;
        networkResult = networkId;
      }
      // 有currency没有network的情况
      if (currencyResult && !networkResult) {
        networkResult = currencyNetworkMap[currencyResult][0].extNetWorkId;
      }
      // 有network没有currency的情况
      if (networkResult && !currencyResult) {
        currencyResult = networkSupportCurrenciesMap[networkResult][0]?.currency || '';
      }
      if (currencyResult) {
        currency$.next(currencyResult);
      }
      if (networkResult) {
        network$.next(networkResult);
      }
    });
}

/**
 * 默认Ethereum链
 * 1. 用户当前钱包连接的链，不在支持的范围内
 * 2. 确保Ethereum链，业务是支持的
 */
function getDefaultNetworkAndCurrency(networkSupportCurrenciesMap) {
  const defaultNetworkId = 1;
  const currencyList = networkSupportCurrenciesMap?.[defaultNetworkId] || [];
  if (currencyList.length > 0) {
    return {
      networkId: defaultNetworkId,
      currency: currencyList[0].currency,
    };
  }
  return {};
}

export function useSafeDepositAccountType(params: {depositAccountType$: DepositAccountType$; currentDepositAddress$: CurrentDepositAddress$; currency$: Currency$; network$: Network$}) {
  const {depositAccountType$, currentDepositAddress$, currency$, network$} = params;
  currentDepositAddress$
    .pipe(
      takeUntil(componentUnMount$),
      repeat({
        delay: () => componentMount$,
      })
    )
    .subscribe((currentDepositAddress) => {
      depositAccountType$.next(currentDepositAddress.accountType);
    });

  depositAccountType$
    .pipe(
      distinctUntilChanged(),
      withLatestFrom(currentDepositAddress$, componentMount$.pipe(delay(3000))),
      filter(([, currentDepositAddress]) => !!currentDepositAddress.address && currency$.getValue() === currentDepositAddress.currency && network$.getValue() === currentDepositAddress.networkId),
      takeUntil(componentUnMount$),
      repeat({
        delay: () => componentMount$,
      })
    )
    .subscribe(([depositAccountType, currentDepositAddress]) => {
      const {chainId, currencyCode, address, tag} = currentDepositAddress;
      updateDepositAccountType({chainId, currencyCode, address, tag, accountType: depositAccountType});
    });
}

export function useSafeDepositAmountEffect({currency$, network$, amount$, depositLimit$}) {
  combineLatest([currency$.pipe(filter(Boolean), distinctUntilChanged()), network$.pipe(filter(Boolean), distinctUntilChanged())])
    .pipe(
      withLatestFrom(amount$.pipe(filter(Boolean))),
      takeUntil(componentUnMount$),
      repeat({
        delay: () => componentMount$,
      })
    )
    .subscribe(() => {
      amount$.next(0);
    });

  amount$
    .pipe(
      filter(Boolean),
      withLatestFrom(depositLimit$),
      takeUntil(componentUnMount$),
      repeat({
        delay: () => componentMount$,
      })
    )
    .subscribe(([amount, depositLimit]) => {
      const {max} = depositLimit || {};
      if (gt(amount, max || 0)) {
        const inputEle = document.querySelector('#chainDepositAmount').querySelector('input');
        const inputValue = parseFloat(max || 0);
        setTimeout(() => {
          amount$.next(inputValue);
          const value = inputValue <= 0 ? '' : `${inputValue}`;
          inputEle.value = value;
          setTimeout(() => {
            inputEle.value = value;
          }, 0);
        }, 0);
      }
    });
}

async function updateDepositAccountType({chainId, currencyCode, address, tag, accountType}) {
  await assetsApi.updateDepositsAccount({
    chainId,
    currency: currencyCode,
    address,
    tag,
    accountType,
  });
}
